/**
 * Lightweight transaction encoder — only depends on @ocap/message/cbor.
 *
 * Cuts out @ocap/asset (~11 MB), @arcblock/did-util, graphql, @arcblock/sdk-util,
 * and the full client/extension.js dependency tree. After the CBOR switch the
 * google-protobuf runtime is no longer pulled in on the client; tree-shaken
 * bundles only include @ocap/proto/schema + cborg (~8 KB).
 *
 * @module @ocap/client/encode
 */
const { toTypeUrl } = require('@ocap/proto/schema');
const { encodeTx: encodeTxCbor } = require('@ocap/message/cbor');

/**
 * Normalize wallet to { address, publicKey }.
 * Handles @ocap/wallet instances, plain objects with pk or publicKey.
 */
function normalizeWallet(wallet) {
  if (!wallet) return { address: '', publicKey: '' };
  if (typeof wallet.toAddress === 'function') {
    return { address: wallet.address, publicKey: wallet.publicKey };
  }
  return {
    address: wallet.address,
    publicKey: wallet.pk || wallet.publicKey || '',
  };
}

/**
 * Encode a transaction to protobuf binary.
 *
 * Pure function — no network IO, no side effects.
 * Logic extracted from extension.js txEncodeFn for standalone use.
 *
 * @param {object} params
 * @param {string} params.type - Transaction type name, e.g. 'TransferV2Tx'
 * @param {object} params.tx - Transaction data (same shape as client.encodeXxxTx({ tx }))
 * @param {object} params.wallet - Wallet { address, publicKey|pk }
 * @param {string} [params.delegator=''] - Delegator address
 * @param {string} [params.chainId=''] - Chain ID (from getChainInfo)
 * @param {Array}  [params.feeConfig=[]] - Fee config [{ typeUrl, fee }] (from getForgeState)
 * @returns {{ object: object, buffer: Buffer }}
 */
function encodeTx({ type, tx, wallet, delegator = '', chainId = '', feeConfig = [] }) {
  const w = normalizeWallet(wallet);
  const address = tx.from || w.address;
  const pk = tx.pk || w.publicKey;
  const nonce = typeof tx.nonce === 'undefined' ? Date.now() : tx.nonce;
  const resolvedChainId = tx.chainId || chainId;

  let signatures = [];
  if (Array.isArray(tx.signatures)) {
    signatures = tx.signatures;
  }
  if (Array.isArray(tx.signaturesList)) {
    signatures = tx.signaturesList;
  }

  // biome-ignore lint/complexity/useOptionalChain: Babel 6 does not support optional chaining
  const itx = tx.itx && tx.itx.typeUrl && tx.itx.value ? tx.itx : { type, value: tx.itx };

  const typeUrl = itx.typeUrl || toTypeUrl(itx.type);
  const fee = feeConfig.find((t) => t.typeUrl === typeUrl);

  const txParams = {
    from: tx.delegator ? address : delegator || address,
    nonce,
    pk,
    chainId: resolvedChainId,
    signature: tx.signature || new Uint8Array(0),
    signatures,
    delegator: tx.delegator || (delegator ? address : ''),
    itx,
    serviceFee: fee ? fee.fee : '0',
  };

  const bytes = encodeTxCbor(txParams);
  return { object: txParams, buffer: Buffer.from(bytes) };
}

// ---- Context fetcher (lightweight replacement for client.getContext) ----

const contextCache = new Map();

/**
 * Fetch minimal chain context (chainId + fee config) via raw HTTP.
 * Cached per endpoint — call clearContextCache() to invalidate.
 *
 * @param {string} chainHost - Chain RPC endpoint URL
 * @returns {Promise<{ chainId: string, txFee: Array }>}
 */
async function fetchContext(chainHost) {
  if (contextCache.has(chainHost)) {
    return contextCache.get(chainHost);
  }

  const post = async (query) => {
    const res = await fetch(chainHost, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ query }),
    });
    if (!res.ok) {
      throw new Error(`fetchContext: HTTP ${res.status} from ${chainHost}`);
    }
    return (await res.json()).data;
  };

  const [info, state] = await Promise.all([
    post('{ getChainInfo { code info { network } } }'),
    post('{ getForgeState { code state { txConfig { txFee { fee typeUrl } } } } }'),
  ]);

  // biome-ignore lint/complexity/useOptionalChain: Babel 6 does not support optional chaining
  const chainInfo = info && info.getChainInfo && info.getChainInfo.info;
  // biome-ignore lint/complexity/useOptionalChain: Babel 6 does not support optional chaining
  const txConfig = state && state.getForgeState && state.getForgeState.state && state.getForgeState.state.txConfig;

  const context = {
    // biome-ignore lint/complexity/useOptionalChain: Babel 6 does not support optional chaining
    chainId: (chainInfo && chainInfo.network) || '',
    // biome-ignore lint/complexity/useOptionalChain: Babel 6 does not support optional chaining
    txFee: (txConfig && txConfig.txFee) || [],
  };

  contextCache.set(chainHost, context);
  return context;
}

function clearContextCache() {
  contextCache.clear();
}

/**
 * Create a TxEncoder function compatible with @arcblock/did-connect-js WalletAuthenticator.
 *
 * Usage:
 *   const { createTxEncoder } = require('@ocap/client/encode');
 *   const authenticator = new WalletAuthenticator({ txEncoder: createTxEncoder(), ... });
 *
 * @returns {function} async ({ type, data, wallet, chainHost }) => Buffer
 */
function createTxEncoder() {
  return async ({ type, data, wallet, chainHost }) => {
    const context = await fetchContext(chainHost);
    const { buffer } = encodeTx({
      type,
      tx: data,
      wallet,
      // biome-ignore lint/complexity/useOptionalChain: Babel 6 does not support optional chaining
      chainId: (data && data.chainId) || context.chainId,
      feeConfig: context.txFee,
    });
    return buffer;
  };
}

module.exports = { encodeTx, createTxEncoder, fetchContext, clearContextCache };
